import { Fragment, useState, useEffect, useRef } from "react";

import proxiableDevices from "../Configuration/ProxiableDevices";
import CellTowerMap from "../FleetManagement/CellTowerMap";
import CompanionDeviceMap from "../FleetManagement/CompanionDeviceMap";
import RadioInformation from "../FleetManagement/RadioInformation";
import RadioDateRangePicker from "../FleetManagement/RadioDateRangePicker";
import FloatingDateRangeWidget from "../FleetManagement/FloatingDateRangeWidget";
import CompanionRadioInformation from "../FleetManagement/CompanionRadioInformation";
import CompanionFunctions from "../FleetManagement/CompanionFunctions";


const CONTENT_MAX_WIDTH_PX = 1200;
const FLOATING_WIDGET_SIDE_SPACE_PX = 170; // minimal margin, overlap on expand is fine

function DeviceManagement(props) {
  const [startDate, setStartDate] = useState(new Date() - 12096e5);
  const [endDate, setEndDate] = useState(new Date());
  const [getByArrivalDate, setGetByArrivalDate] = useState(true);
  const [refreshCount, setRefreshCount] = useState(0);
  const forceRefresh = () => setRefreshCount((c) => c + 1);

  const pickerRef = useRef(null);
  const [pickerScrolledOutOfView, setPickerScrolledOutOfView] = useState(false);
  const [hasSideSpaceForWidget, setHasSideSpaceForWidget] = useState(
    window.innerWidth - CONTENT_MAX_WIDTH_PX >= FLOATING_WIDGET_SIDE_SPACE_PX * 2
  );

  useEffect(() => {
    const handleResize = () =>
      setHasSideSpaceForWidget(
        window.innerWidth - CONTENT_MAX_WIDTH_PX >= FLOATING_WIDGET_SIDE_SPACE_PX * 2
      );
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (!pickerRef.current || !props.focused) {
      // Only track while this tab is actually the active one 
      return undefined;
    }

    setPickerScrolledOutOfView(false);

    let isFirstCallback = true;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (isFirstCallback) {
          isFirstCallback = false;
          return;
        }
        setPickerScrolledOutOfView(!entry.isIntersecting);
      },
      { threshold: 0 }
    );
    observer.observe(pickerRef.current);
    return () => observer.disconnect();
  }, [props.type, props.focused]);

  const showFloatingWidget =
    props.focused && pickerScrolledOutOfView && hasSideSpaceForWidget;

  return (
    <Fragment>
      <div ref={pickerRef}>
        <RadioDateRangePicker
          startDate={startDate}
          endDate={endDate}
          onStartDateChange={setStartDate}
          onEndDateChange={setEndDate}
          getByArrivalDate={getByArrivalDate}
          onGetByArrivalDateChange={setGetByArrivalDate}
          onRefresh={forceRefresh}
        />
      </div>
      {showFloatingWidget && (
        <FloatingDateRangeWidget
          startDate={startDate}
          endDate={endDate}
          onStartDateChange={setStartDate}
          onEndDateChange={setEndDate}
          getByArrivalDate={getByArrivalDate}
          onGetByArrivalDateChange={setGetByArrivalDate}
          onRefresh={forceRefresh}
        />
      )}
      {proxiableDevices.includes(props.type) ? (
        <CompanionDeviceMap
          coreId={props.coreId}
          virtualId={props.virtualId}
          focused={props.focused}
          startDate={startDate}
          endDate={endDate}
        />
      ) : (
        <CellTowerMap
          coreId={props.coreId}
          virtualId={props.virtualId}
          focused={props.focused}
          startDate={startDate}
          endDate={endDate}
        />
      )}
      {proxiableDevices.includes(props.type) ? null : (
        <RadioInformation
          coreId={props.coreId}
          focused={props.focused}
          productType={props.type}
          startDate={startDate}
          endDate={endDate}
          onStartDateChange={setStartDate}
          onEndDateChange={setEndDate}
          getByArrivalDate={getByArrivalDate}
          onGetByArrivalDateChange={setGetByArrivalDate}
          refreshCount={refreshCount}
          onRefresh={forceRefresh}
          showDatePicker={false}
        />
      )}
      {proxiableDevices.includes(props.type) ? (
        <CompanionRadioInformation
          coreId={props.coreId}
          productType={props.type}
          startDate={startDate}
          endDate={endDate}
          getByArrivalDate={getByArrivalDate}
          refreshCount={refreshCount}
        />
      ) : null}
      {proxiableDevices.includes(props.type) ? (
        <CompanionFunctions coreId={props.coreId} deviceType={props.type} />
      ) : null}
    </Fragment>
  );
}

export default DeviceManagement;
